import { NearbyProject } from "@/hooks/use-nearby-projects";
import { logger } from "@/utils/logger";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { memo, useCallback, useMemo } from "react";
import { ClusterMarker } from "./cluster-marker";

interface MapMarkerClustererProps {
	projects: NearbyProject[];
	isHovered: number | null;
	setIsHovered: (index: number | null) => void;
	mapZoom: number;
	handleProjectClick: (project: NearbyProject) => void;
	setMapCenter: (center: { lat: number; lng: number }) => void;
	setMapZoom: (zoom: number) => void;
	isMobile?: boolean;
}

// Memoized single marker component
const SingleMarker = memo(
	({
		cluster,
		isMobile,
		onProjectClick,
	}: {
		cluster: ClusterData;
		isMobile: boolean;
		onProjectClick: (project: NearbyProject) => void;
	}) => {
		const handleClick = useCallback(
			(e: React.MouseEvent) => {
				e.stopPropagation();
				onProjectClick(cluster.points[0]);
			},
			[cluster.points, onProjectClick],
		);

		return (
			<AdvancedMarker position={cluster.center} zIndex={10} clickable={true} className="group">
				<div>
					<div onClick={handleClick}>
						<ClusterMarker cluster={cluster} />
					</div>
				</div>

				<div
					onClick={handleClick}
					className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 opacity-100 transition-opacity duration-200 z-10"
				>
					<div className="rounded-lg shadow-lg border border-gray-200 p-1 max-w-48 cursor-pointer hover:bg-gray-50 bg-white">
						<div className="text-xs font-small text-primary-700 truncate">{cluster.points[0].name}</div>
					</div>
				</div>
			</AdvancedMarker>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison to prevent unnecessary re-renders
		return (
			prevProps.cluster.center.lat === nextProps.cluster.center.lat &&
			prevProps.cluster.center.lng === nextProps.cluster.center.lng &&
			prevProps.cluster.count === nextProps.cluster.count &&
			prevProps.isMobile === nextProps.isMobile &&
			prevProps.cluster.points[0]?.id === nextProps.cluster.points[0]?.id
		);
	},
);

SingleMarker.displayName = "SingleMarker";

// Memoized cluster marker component
const ClusteredMarker = memo(
	({
		cluster,
		mapZoom,
		onClusterClick,
	}: {
		cluster: ClusterData;
		mapZoom: number;
		onClusterClick: (center: { lat: number; lng: number }, zoom: number) => void;
	}) => {
		const handleClick = useCallback(
			(e: React.MouseEvent) => {
				e.stopPropagation();
				onClusterClick(cluster.center, Math.max(mapZoom + 1, 18));
			},
			[cluster.center, mapZoom, onClusterClick],
		);

		return (
			<AdvancedMarker position={cluster.center} zIndex={100} clickable={true} className="group">
				<div>
					<div onClick={handleClick}>
						<ClusterMarker cluster={cluster} />
					</div>
				</div>
			</AdvancedMarker>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.cluster.center.lat === nextProps.cluster.center.lat &&
			prevProps.cluster.center.lng === nextProps.cluster.center.lng &&
			prevProps.cluster.count === nextProps.cluster.count &&
			prevProps.mapZoom === nextProps.mapZoom
		);
	},
);

ClusteredMarker.displayName = "ClusteredMarker";

type ClusterData = {
	points: NearbyProject[];
	center: { lat: number; lng: number };
	count: number;
};

// Helper function moved outside component to prevent recreation
const isValidCoordinates = (project: NearbyProject): boolean => {
	try {
		if (!project?.location?.gps?.coordinates) {
			logger.error(`Project ${project?.id || "unknown"} missing GPS coordinates`);
			return false;
		}

		const coords = project.location.gps.coordinates;
		if (!Array.isArray(coords) || coords.length < 2) {
			logger.error(`Project ${project.id} has invalid coordinates array:`, coords);
			return false;
		}

		const [lng, lat] = coords;
		if (typeof lng !== "number" || typeof lat !== "number") {
			logger.error(`Project ${project.id} has non-numeric coordinates: lng=${lng}, lat=${lat}`);
			return false;
		}

		if (!isFinite(lng) || !isFinite(lat)) {
			logger.error(`Project ${project.id} has invalid numeric coordinates: lng=${lng}, lat=${lat}`);
			return false;
		}

		if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
			logger.error(`Project ${project.id} coordinates out of bounds: lng=${lng}, lat=${lat}`);
			return false;
		}

		return true;
	} catch (error) {
		logger.error(`Error validating coordinates for project ${project?.id || "unknown"}:`, error);
		return false;
	}
};

export const MapMarkerClusterer = memo(
	({ projects, mapZoom, handleProjectClick, setMapCenter, setMapZoom, isMobile = false }: MapMarkerClustererProps) => {
		// Memoize the clustering algorithm with proper dependencies
		const map = useMap();
		const clusters = useMemo(() => {
			if (!projects.length) return [];

			const baseGridSize = isMobile ? 20000 : 500;
			const zoomFactor = Math.pow(2, 14 - mapZoom);
			const gridSize = Math.max(80, baseGridSize * zoomFactor);

			const clusterArray: ClusterData[] = [];
			const validProjects = projects.filter(isValidCoordinates);

			if (validProjects.length === 0) {
				logger.error("No projects with valid GPS coordinates found for clustering");
				return [];
			}

			validProjects.forEach(project => {
				if (project.location?.gps?.coordinates?.length !== 2) return;
				const lat = project.location.gps.coordinates[1];
				const lng = project.location.gps.coordinates[0];

				let found = false;
				for (const cluster of clusterArray) {
					const distance = Math.sqrt(
						Math.pow((cluster.center.lat - lat) * 111000, 2) +
							Math.pow((cluster.center.lng - lng) * 111000 * Math.cos((lat * Math.PI) / 180), 2),
					);

					if (distance < gridSize) {
						cluster.points.push(project);
						const totalLat = cluster.points.reduce((sum, p) => sum + p.location.gps.coordinates[1], 0);
						const totalLng = cluster.points.reduce((sum, p) => sum + p.location.gps.coordinates[0], 0);
						cluster.center = {
							lat: totalLat / cluster.points.length,
							lng: totalLng / cluster.points.length,
						};
						cluster.count = cluster.points.length;
						found = true;
						break;
					}
				}

				if (!found) {
					clusterArray.push({
						points: [project],
						center: { lat, lng },
						count: 1,
					});
				}
			});

			return clusterArray;
		}, [projects, isMobile, mapZoom]);

		// Memoize callbacks to prevent child re-renders
		const handleClusterClick = useCallback(
			async (center: { lat: number; lng: number }, zoom: number) => {
				setMapCenter(center);
				await map?.panTo(center);
				setMapZoom(zoom);
			},
			[map, setMapCenter, setMapZoom],
		);

		return (
			<>
				{clusters.map((cluster, clusterIndex) => {
					if (cluster.count === 1) {
						return (
							<SingleMarker
								key={`single-${cluster.points[0].id}`}
								cluster={cluster}
								isMobile={isMobile}
								onProjectClick={handleProjectClick}
							/>
						);
					}

					return (
						<ClusteredMarker
							key={`cluster-${clusterIndex}-${cluster.count}`}
							cluster={cluster}
							mapZoom={mapZoom}
							onClusterClick={handleClusterClick}
						/>
					);
				})}
			</>
		);
	},
);

MapMarkerClusterer.displayName = "MapMarkerClusterer";
